import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, MapPin } from "lucide-react";
import { useSettings } from "../lib/settings";
import { siteText } from "../lib/siteTexts";

export default function Footer() {
  const s = useSettings();
  const toursEmail = siteText(s, "footer_tours_email");
  const year = new Date().getFullYear();
  const copyright = (s.footer_copyright || "").replace(/^©\s*/, "");

  const socials = [
    { url: s.footer_social_instagram, Icon: Instagram, label: "Instagram", testid: "footer-instagram" },
    { url: s.footer_social_facebook, Icon: Facebook, label: "Facebook", testid: "footer-facebook" },
    { url: s.footer_social_youtube, Icon: Youtube, label: "YouTube", testid: "footer-youtube" },
  ].filter((x) => x.url);

  return (
    <footer
      className="mt-24 border-t"
      style={{ backgroundColor: "#1A2F1C", color: "#F5F0E8", borderColor: "var(--wp-primary-hover)" }}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand + Social */}
          <div>
            <img
              src={s.footer_logo_url || "/logos/logo-v3-white.png"}
              alt={s.app_name || "AdventureCouple.lu"}
              className="h-16 md:h-20 w-auto mb-5"
            />
            {s.footer_quote && (
              <p
                className="font-display text-lg md:text-xl italic leading-snug mb-4 whitespace-pre-line"
                style={{ color: "var(--wp-medicinal)" }}
                data-testid="footer-quote"
              >
                {s.footer_quote}
              </p>
            )}
            {s.footer_tagline && (
              <p className="text-sm opacity-80 leading-relaxed" data-testid="footer-tagline">
                {s.footer_tagline}
              </p>
            )}
            {socials.length > 0 && (
              <div className="flex gap-3 mt-6">
                {socials.map(({ url, Icon, label, testid }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={testid}
                    className="w-9 h-9 rounded-full border border-[var(--wp-cream)]/30 flex items-center justify-center hover:bg-[var(--wp-cream)]/10"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Entdecken */}
          <div>
            <h4 className="overline mb-4" style={{ color: "var(--wp-medicinal)" }}>Entdecken</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/plants" className="opacity-80 hover:opacity-100" data-testid="footer-plants">Wildkräuter-Datenbank</Link></li>
              <li><Link to="/identify" className="opacity-80 hover:opacity-100">Pflanze erkennen</Link></li>
              <li><Link to="/calendar" className="opacity-80 hover:opacity-100">Saisonkalender</Link></li>
              <li><Link to="/compare" className="opacity-80 hover:opacity-100">Vergleichs-Modus</Link></li>
              <li><Link to="/coach" className="opacity-80 hover:opacity-100">KI-Naturcoach</Link></li>
              <li><Link to="/magazin" className="opacity-80 hover:opacity-100" data-testid="footer-magazin">Magazin & Wissen</Link></li>
              <li><Link to="/downloads" className="opacity-80 hover:opacity-100" data-testid="footer-downloads">Downloads (PDFs)</Link></li>
            </ul>
          </div>

          {/* Mitmachen */}
          <div>
            <h4 className="overline mb-4" style={{ color: "var(--wp-medicinal)" }}>Mitmachen</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/tours" className="opacity-80 hover:opacity-100" data-testid="footer-tours">Geführte Touren</Link></li>
              <li><Link to="/community" className="opacity-80 hover:opacity-100">Community-Rezepte</Link></li>
              <li><Link to="/journal" className="opacity-80 hover:opacity-100">Tagebuch</Link></li>
              <li><Link to="/quiz" className="opacity-80 hover:opacity-100">Pflanzen-Quiz</Link></li>
              <li><Link to="/register" className="opacity-80 hover:opacity-100">Account erstellen</Link></li>
              <li>
                <a
                  href="https://freespirit.clothing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-80 hover:opacity-100 inline-flex items-center gap-1"
                  data-testid="footer-shop"
                  style={{ color: "var(--wp-medicinal)", fontWeight: 500 }}
                >
                  Freespirit Clothing <span className="text-xs">↗</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="overline mb-4" style={{ color: "var(--wp-medicinal)" }}>Kontakt</h4>
            <ul className="space-y-3 text-sm">
              {s.footer_location && (
                <li className="flex items-start gap-2 opacity-80"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {s.footer_location}</li>
              )}
              <li className="flex items-start gap-2 opacity-80" data-testid="footer-contact-email"><Mail className="w-4 h-4 mt-0.5 shrink-0" /> <a href={`mailto:${toursEmail}`} className="hover:opacity-100">{toursEmail}</a></li>
              <li className="opacity-80" data-testid="footer-more-info">Mehr Infos: <a href={siteText(s, "footer_more_info_url")} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-100">{siteText(s, "footer_more_info_label")}</a></li>
              <li><Link to="/contact" className="opacity-80 hover:opacity-100" data-testid="footer-contact">Kontaktformular →</Link></li>
              <li><Link to="/partner" className="opacity-80 hover:opacity-100" data-testid="footer-partner">Für Partner & Presse</Link></li>
              <li><Link to="/impressum" className="opacity-80 hover:opacity-100">Impressum & Datenschutz</Link></li>
            </ul>
          </div>
        </div>

        <div className="section-divider my-10" style={{ background: "linear-gradient(to right, transparent, rgba(245,240,232,0.2), transparent)" }} />

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs opacity-70">
          <div data-testid="footer-copyright">© {year} {copyright}</div>
          {s.footer_motto && <div data-testid="footer-motto">{s.footer_motto}</div>}
        </div>
      </div>
    </footer>
  );
}
